import { isStory3ExpertId } from '../data/story3/experts';
import type {
  Story3ExpertId,
  Story3FeedbackModeId,
  Story3NotebookEntry,
} from '../types';

export const STORY3_NOTEBOOK_MAX_CHARS = 4_000;
export const STORY3_NOTEBOOK_MAX_ENTRIES = 30;
export const STORY3_NOTEBOOK_TITLE_MAX = 64;

const FEEDBACK_MODES: readonly Story3FeedbackModeId[] = [
  'correction',
  'assessment',
  'question',
  'expert',
];

function isFeedbackMode(raw: unknown): raw is Story3FeedbackModeId {
  return (
    typeof raw === 'string' &&
    (FEEDBACK_MODES as readonly string[]).includes(raw)
  );
}

function newNotebookId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `nb-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function notebookExcerpt(text: string, max = 120): string {
  const oneLine = text.replace(/\s+/g, ' ').trim();
  if (oneLine.length <= max) return oneLine;
  return `${oneLine.slice(0, max - 1)}…`;
}

export function notebookTitleFromText(
  text: string,
  max = STORY3_NOTEBOOK_TITLE_MAX,
): string {
  const line =
    text.split('\n').find((l) => l.trim().length > 0)?.trim() ?? text.trim();
  if (!line) return 'Uden titel';
  if (line.length <= max) return line;
  return `${line.slice(0, max - 1)}…`;
}

export function normalizeStory3NotebookEntry(
  raw: unknown,
): Story3NotebookEntry | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.id !== 'string' || o.id.length === 0) return null;
  if (typeof o.text !== 'string') return null;

  const text = o.text.slice(0, STORY3_NOTEBOOK_MAX_CHARS);
  if (text.trim().length === 0) return null;

  const title =
    typeof o.title === 'string' && o.title.trim().length > 0
      ? o.title.trim().slice(0, STORY3_NOTEBOOK_TITLE_MAX)
      : notebookTitleFromText(text);
  const savedAt =
    typeof o.savedAt === 'string' && o.savedAt.length > 0
      ? o.savedAt
      : new Date().toISOString();
  const updatedAt =
    typeof o.updatedAt === 'string' && o.updatedAt.length > 0
      ? o.updatedAt
      : savedAt;

  const entry: Story3NotebookEntry = {
    id: o.id,
    title,
    text,
    savedAt,
    updatedAt,
  };
  if (isFeedbackMode(o.mode)) entry.mode = o.mode;
  if (typeof o.expert === 'string' && isStory3ExpertId(o.expert)) {
    entry.expert = o.expert;
  }
  if (typeof o.customExpert === 'string' && o.customExpert.trim().length > 0) {
    entry.customExpert = o.customExpert.trim().slice(0, 48);
  }
  if (typeof o.lastFeedback === 'string' && o.lastFeedback.trim().length > 0) {
    entry.lastFeedback = o.lastFeedback.trim().slice(0, 8_000);
  }
  return entry;
}

/** Legacy single draft → one notebook entry (one-time migration). */
export function notebookEntryFromLegacyDraft(raw: unknown): Story3NotebookEntry | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.text !== 'string' || o.text.trim().length === 0) return null;
  const now = new Date().toISOString();
  return (
    normalizeStory3NotebookEntry({
      id: newNotebookId(),
      title: notebookTitleFromText(o.text),
      text: o.text,
      savedAt: typeof o.updatedAt === 'string' ? o.updatedAt : now,
      updatedAt: typeof o.updatedAt === 'string' ? o.updatedAt : now,
      mode: o.mode,
      expert: o.expert,
      customExpert: o.customExpert,
      lastFeedback: o.lastFeedback,
    }) ?? null
  );
}

export function normalizeStory3Notebook(raw: unknown): Story3NotebookEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: Story3NotebookEntry[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const entry = normalizeStory3NotebookEntry(item);
    if (!entry || seen.has(entry.id)) continue;
    seen.add(entry.id);
    out.push(entry);
    if (out.length >= STORY3_NOTEBOOK_MAX_ENTRIES) break;
  }
  return out.sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
}

export function buildNotebookEntry(input: {
  id?: string;
  title?: string;
  text: string;
  mode?: Story3FeedbackModeId | null;
  expert?: Story3ExpertId | null;
  customExpert?: string | null;
  lastFeedback?: string | null;
  savedAt?: string;
}): Story3NotebookEntry | null {
  const text = input.text.slice(0, STORY3_NOTEBOOK_MAX_CHARS);
  if (text.trim().length === 0) return null;
  const now = new Date().toISOString();
  return normalizeStory3NotebookEntry({
    id: input.id ?? newNotebookId(),
    title: input.title ?? notebookTitleFromText(text),
    text,
    savedAt: input.savedAt ?? now,
    updatedAt: now,
    mode: input.mode ?? undefined,
    expert: input.expert ?? undefined,
    customExpert: input.customExpert ?? undefined,
    lastFeedback: input.lastFeedback ?? undefined,
  });
}

export function upsertNotebookEntry(
  notebook: Story3NotebookEntry[],
  entry: Story3NotebookEntry,
): Story3NotebookEntry[] {
  const without = notebook.filter((e) => e.id !== entry.id);
  return [entry, ...without]
    .sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    )
    .slice(0, STORY3_NOTEBOOK_MAX_ENTRIES);
}

export function deleteNotebookEntry(
  notebook: Story3NotebookEntry[],
  id: string,
): Story3NotebookEntry[] {
  return notebook.filter((e) => e.id !== id);
}
